import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Download, ExternalLink, Search } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';

interface PromoData {
  id: string;
  ad_name: string | null;
  offer_text: string | null;
  company_name: string;
  views: number;
  clicks: number;
  redemptions: number;
  created_at: string;
  end_date: string;
  is_active: boolean;
  cta_url: string;
}

const AdminPromos: React.FC = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  
  const [promosData, setPromosData] = useState<PromoData[]>([]);
  const [filteredData, setFilteredData] = useState<PromoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdvertiser, setSelectedAdvertiser] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{start?: Date; end?: Date}>({});

  // Get unique advertisers for filter
  const advertisers = [...new Set(promosData.map(promo => promo.company_name))];

  useEffect(() => {
    const fetchPromosData = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('ads')
          .select(`
            id,
            ad_name,
            offer_text,
            company_name,
            views,
            clicks,
            redemptions,
            created_at,
            end_date,
            is_active,
            cta_url
          `)
          .eq('is_test', false)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setPromosData(data || []);
      } catch (error) {
        console.error('Error fetching promos data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPromosData();
  }, [user]);

  // Filter data based on search term, advertiser, status, and date range
  useEffect(() => {
    let filtered = promosData;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(promo => 
        (promo.ad_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         promo.offer_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         promo.company_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Advertiser filter
    if (selectedAdvertiser !== 'all') {
      filtered = filtered.filter(promo => promo.company_name === selectedAdvertiser);
    }

    // Status filter
    if (selectedStatus === 'active') {
      filtered = filtered.filter(promo => promo.is_active && new Date(promo.end_date) >= new Date());
    } else if (selectedStatus === 'expired') {
      filtered = filtered.filter(promo => !promo.is_active || new Date(promo.end_date) < new Date());
    }

    // Date range filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter(promo => {
        const promoDate = new Date(promo.created_at);
        return promoDate >= dateRange.start! && promoDate <= dateRange.end!;
      });
    }

    setFilteredData(filtered);
  }, [promosData, searchTerm, selectedAdvertiser, selectedStatus, dateRange]);

  const calculateEngagementRate = (clicks: number, views: number) => {
    return views > 0 ? ((clicks / views) * 100).toFixed(2) : '0.00';
  };

  const calculateRedemptionRate = (redemptions: number, views: number) => {
    return views > 0 ? ((redemptions / views) * 100).toFixed(2) : '0.00';
  };

  const exportToCSV = () => {
    const headers = ['Promo Title', 'Advertiser', 'Views', 'Clicks', 'Redemptions', 'Engagement Rate (%)', 'Redemption Rate (%)', 'Created', 'Status'];
    const csvData = filteredData.map(promo => [
      promo.ad_name || promo.offer_text || 'Sin título',
      promo.company_name,
      promo.views || 0,
      promo.clicks || 0,
      promo.redemptions || 0,
      calculateEngagementRate(promo.clicks || 0, promo.views || 0),
      calculateRedemptionRate(promo.redemptions || 0, promo.views || 0),
      format(new Date(promo.created_at), 'dd/MM/yyyy'),
      promo.is_active && new Date(promo.end_date) >= new Date() ? 'Active' : 'Expired'
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `promos_analytics_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">
          {isSpanish ? 'Analytics de Promociones' : 'Promos Analytics'}
        </h1>
        <Button onClick={exportToCSV} variant="outline" className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          {isSpanish ? 'Exportar CSV' : 'Export CSV'}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>{isSpanish ? 'Filtros' : 'Filters'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={isSpanish ? 'Buscar promociones...' : 'Search promos...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Advertiser Filter */}
            <Select value={selectedAdvertiser} onValueChange={setSelectedAdvertiser}>
              <SelectTrigger>
                <SelectValue placeholder={isSpanish ? 'Anunciante' : 'Advertiser'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
                {advertisers.map(advertiser => (
                  <SelectItem key={advertiser} value={advertiser}>
                    {advertiser}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder={isSpanish ? 'Estado' : 'Status'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isSpanish ? 'Todos' : 'All'}</SelectItem>
                <SelectItem value="active">{isSpanish ? 'Activos' : 'Active'}</SelectItem>
                <SelectItem value="expired">{isSpanish ? 'Expirados' : 'Expired'}</SelectItem>
              </SelectContent>
            </Select>

            {/* Date Range */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn("justify-start text-left font-normal", !dateRange.start && "text-muted-foreground")}>
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.start ? (
                    dateRange.end ? (
                      `${format(dateRange.start, "dd/MM/yy")} - ${format(dateRange.end, "dd/MM/yy")}`
                    ) : (
                      format(dateRange.start, "dd/MM/yyyy")
                    )
                  ) : (
                    <span>{isSpanish ? 'Rango de fechas' : 'Date range'}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.start, to: dateRange.end }}
                  onSelect={(range) => setDateRange({ start: range?.from, end: range?.to })}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {isSpanish ? 'Rendimiento de Promociones' : 'Promo Performance'}
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              ({filteredData.length} {isSpanish ? 'promociones' : 'promos'})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  <TableHead>{isSpanish ? 'Título' : 'Title'}</TableHead>
                  <TableHead>{isSpanish ? 'Anunciante' : 'Advertiser'}</TableHead>
                  <TableHead className="text-center">{isSpanish ? 'Vistas' : 'Views'}</TableHead>
                  <TableHead className="text-center">{isSpanish ? 'Clicks' : 'Clicks'}</TableHead>
                  <TableHead className="text-center">{isSpanish ? 'Redenciones' : 'Redemptions'}</TableHead>
                  <TableHead className="text-center">{isSpanish ? 'Engagement (%)' : 'Engagement (%)'}</TableHead>
                  <TableHead className="text-center">{isSpanish ? 'Redención (%)' : 'Redemption (%)'}</TableHead>
                  <TableHead>{isSpanish ? 'Estado' : 'Status'}</TableHead>
                  <TableHead>{isSpanish ? 'Creado' : 'Created'}</TableHead>
                  <TableHead>{isSpanish ? 'Acciones' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      {isSpanish ? 'No hay promociones disponibles' : 'No promos available'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((promo) => {
                    const isActive = promo.is_active && new Date(promo.end_date) >= new Date();
                    const engagementRate = calculateEngagementRate(promo.clicks || 0, promo.views || 0);
                    const redemptionRate = calculateRedemptionRate(promo.redemptions || 0, promo.views || 0);

                    return (
                      <TableRow key={promo.id} className="hover:bg-muted/50">
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {promo.ad_name || promo.offer_text || (isSpanish ? 'Sin título' : 'No title')}
                        </TableCell>
                        <TableCell className="cursor-pointer hover:text-primary hover:underline"
                          onClick={() => setSelectedAdvertiser(promo.company_name)}>
                          {promo.company_name}
                        </TableCell>
                        <TableCell className="text-center">{promo.views || 0}</TableCell>
                        <TableCell className="text-center">{promo.clicks || 0}</TableCell>
                        <TableCell className="text-center">{promo.redemptions || 0}</TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            "font-medium",
                            parseFloat(engagementRate) > 5 ? "text-green-600" : 
                            parseFloat(engagementRate) > 2 ? "text-yellow-600" : "text-red-600"
                          )}>
                            {engagementRate}%
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={cn(
                            "font-medium",
                            parseFloat(redemptionRate) > 2 ? "text-green-600" : 
                            parseFloat(redemptionRate) > 1 ? "text-yellow-600" : "text-red-600"
                          )}>
                            {redemptionRate}%
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={isActive ? "default" : "secondary"}>
                            {isActive ? (isSpanish ? 'Activa' : 'Active') : (isSpanish ? 'Expirada' : 'Expired')}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(promo.created_at)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            asChild
                            className="flex items-center gap-1"
                          >
                            <a href={promo.cta_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3 w-3" />
                              {isSpanish ? 'Ver' : 'View'}
                            </a>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPromos;